import React, { useState } from 'react';
import {
  KeyRound, RefreshCw, Link2, Download, CheckCircle2,
  Eye, Heart, AlertTriangle, ImageIcon
} from 'lucide-react';
import { collectService } from '../../services/collectService';
import { useTranslation } from '../../i18n/LocaleContext';

const AUTO_PLATFORMS = [
  { key: 'youtube', label: 'YouTube', auto: true },
  { key: 'tiktok', label: 'TikTok', auto: true },
  { key: 'instagram', label: 'Instagram', auto: false },
  { key: 'facebook', label: 'Facebook', auto: false }
];

const getActionOptions = (t) => [
  { value: 'like', label: t('aClActLike') },
  { value: 'comment', label: t('aClActComment') },
  { value: 'subscribe', label: t('aClActSubscribe') },
  { value: 'follow', label: t('aClActFollow') },
  { value: 'share', label: t('aClActShare') },
  { value: 'review', label: t('aClActReview') },
  { value: 'join', label: t('aClActJoin') }
];

const CATEGORY_OPTIONS = ['', 'Friends', 'Hobbies', 'Photography', 'Beauty', 'Business'];

const formatCount = (n) => {
  const v = Number(n) || 0;
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const decorate = (items) =>
  items.map((it, i) => ({
    ...it,
    key: `${it.url}#${i}`,
    reward: 0.5,
    daily_quota: 100,
    per_user_daily_limit: 1,
    category: '',
    selected: true
  }));

const AdminCollect = () => {
  const { t } = useTranslation();
  const [platform, setPlatform] = useState('youtube');
  const [limit, setLimit] = useState(10);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyLoaded, setApiKeyLoaded] = useState(false);
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeyMsg, setApiKeyMsg] = useState('');
  const [linkBox, setLinkBox] = useState('');

  React.useEffect(() => {
    collectService
      .getConfig()
      .then((d) => {
        setApiKey(d.youtube_api_key || '');
        setApiKeyLoaded(true);
      })
      .catch(() => setApiKeyLoaded(true));
  }, []);

  const errText = (e) => {
    switch (e.code) {
      case 'youtube_no_key': return t('aClErrNoKey');
      case 'tiktok_no_data': return t('aClErrTiktok');
      case 'no_free_api': return t('aClNoTrendingApi', { platform: AUTO_PLATFORMS.find((pp) => pp.key === platform)?.label || '' });
      case 'missing_urls': return t('aClErrNoLinks');
      case 'too_many_urls': return t('aClErrTooManyLinks');
      case 'too_many_items': return t('aClErrTooManyItems');
      default: return e.message;
    }
  };

  const saveApiKey = async () => {
    setApiKeySaving(true);
    setApiKeyMsg('');
    try {
      await collectService.saveConfig(apiKey.trim());
      setApiKeyMsg(apiKey.trim() ? t('aClKeySaved') : t('aClKeyCleared'));
    } catch (e) {
      setApiKeyMsg(e.message);
    } finally {
      setApiKeySaving(false);
    }
  };

  const runFetch = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const d = await collectService.fetchTrending(platform, limit);
      setCandidates(decorate(d.items || []));
    } catch (e) {
      setError(errText(e));
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const runResolve = async () => {
    const urls = linkBox.split(/\s+/).map((u) => u.trim()).filter(Boolean);
    if (!urls.length) {
      setError(t('aClPasteLinksFirst'));
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const d = await collectService.resolveLinks(urls);
      setCandidates(decorate(d.items || []));
    } catch (e) {
      setError(errText(e));
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  };

  const patchCandidate = (key, patch) =>
    setCandidates((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const toggleAll = (checked) =>
    setCandidates((prev) => prev.map((c) => ({ ...c, selected: checked })));

  const selected = candidates.filter((c) => c.selected);

  const runImport = async () => {
    if (!selected.length) return;
    setImporting(true);
    setError('');
    setResult(null);
    try {
      const items = selected.map((c) => ({
        platform: c.platform,
        action_type: c.action_type,
        title: c.title,
        target_url: c.url,
        thumb_url: c.thumb_url,
        video_url: c.video_url || '',
        reward: c.reward,
        daily_quota: c.daily_quota,
        per_user_daily_limit: c.per_user_daily_limit,
        category: c.category
      }));
      const d = await collectService.importTasks(items);
      setResult(d);
      setCandidates((prev) => prev.filter((c) => !c.selected));
    } catch (e) {
      setError(errText(e));
    } finally {
      setImporting(false);
    }
  };

  const actionOptions = getActionOptions(t);

  const inputCls =
    'bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500';
  const selectCls = inputCls;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aClTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aClSubtitle')}</p>
      </div>

      {/* YouTube API key */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-brand-400" />
          <h2 className="font-semibold text-white">YouTube Data API key</h2>
          {apiKeyLoaded && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${apiKey ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
              {apiKey ? t('aClApiKeyConfigured') : t('aClApiKeyNotConfigured')}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="AIza..."
            className={`${inputCls} flex-1 min-w-[240px]`}
          />
          <button
            onClick={saveApiKey}
            disabled={apiKeySaving}
            className="px-4 py-2 rounded-lg bg-brand-500 text-white font-medium text-sm hover:bg-brand-600 disabled:opacity-50"
          >
            {apiKeySaving ? t('aClSaving') : t('aClSaveKey')}
          </button>
        </div>
        {apiKeyMsg && <p className="text-xs text-gray-400 mt-2">{apiKeyMsg}</p>}
        <p className="text-xs text-gray-500 mt-2">{t('aClApiKeyHint')}</p>
      </div>

      {/* Platform + fetch */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2">
          {AUTO_PLATFORMS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPlatform(p.key)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                platform === p.key ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex-1" />
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className={selectCls}>
            <option value={10}>{t('aClNResults', { n: 10 })}</option>
            <option value={20}>{t('aClNResults', { n: 20 })}</option>
            <option value={30}>{t('aClNResults', { n: 30 })}</option>
          </select>
          <button
            onClick={runFetch}
            disabled={loading || !AUTO_PLATFORMS.find((p) => p.key === platform)?.auto}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white font-medium text-sm hover:bg-brand-600 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            {t('aClFetchTrending')}
          </button>
        </div>
        {!AUTO_PLATFORMS.find((p) => p.key === platform)?.auto && (
          <p className="text-xs text-gray-500 mt-3 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />
            {t('aClNoTrendingApi', { platform: AUTO_PLATFORMS.find((p) => p.key === platform)?.label })}
          </p>
        )}
      </div>

      {/* Link import */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-brand-400" />
          <h2 className="font-semibold text-white">{t('aClLinkImport')}</h2>
          <span className="text-xs text-gray-500">{t('aClLinkImportHint')}</span>
        </div>
        <textarea
          value={linkBox}
          onChange={(e) => setLinkBox(e.target.value)}
          rows={3}
          placeholder={t('aClLinksPlaceholder')}
          className={`${inputCls} w-full resize-y`}
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={runResolve}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-700 text-white font-medium text-sm hover:bg-gray-600 disabled:opacity-50"
          >
            <Link2 className="w-4 h-4" />
            {t('aClAnalyzeLinks')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/40 text-red-400 rounded-xl p-4 mb-6 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-green-500/10 border border-green-500/40 text-green-400 rounded-xl p-4 mb-6 text-sm flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5" />
          {t('aClImportedN', { n: result.imported })}
          {result.skipped ? t('aClSkippedN', { n: result.skipped }) : ''}
          {result.errors?.length ? t('aClErrors', { list: result.errors.join('; ') }) : ''}
        </div>
      )}

      {/* Candidates */}
      {candidates.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.length === candidates.length}
                onChange={(e) => toggleAll(e.target.checked)}
                className="w-4 h-4 accent-brand-500"
              />
              {t('aClSelectAll', { sel: selected.length, total: candidates.length })}
            </label>
            <button
              onClick={runImport}
              disabled={importing || !selected.length}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white font-medium text-sm hover:bg-brand-600 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {importing ? t('aClImporting') : t('aClImportN', { n: selected.length })}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-900 border-b border-gray-700 text-left text-xs text-gray-400 uppercase">
                <tr>
                  <th className="p-3 w-10"></th>
                  <th className="p-3">{t('aClColTask')}</th>
                  <th className="p-3">{t('aClColAction')}</th>
                  <th className="p-3">{t('aClColCategory')}</th>
                  <th className="p-3 w-24">{t('aClColReward')}</th>
                  <th className="p-3 w-28">{t('aClColQuota')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {candidates.map((c) => (
                  <tr key={c.key} className={!c.selected ? 'opacity-45' : ''}>
                    <td className="p-3 align-top">
                      <input
                        type="checkbox"
                        checked={c.selected}
                        onChange={(e) => patchCandidate(c.key, { selected: e.target.checked })}
                        className="w-4 h-4 accent-brand-500 mt-1"
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex gap-3">
                        <div className="w-20 h-14 rounded-lg bg-gray-900 overflow-hidden shrink-0 flex items-center justify-center">
                          {c.thumb_url ? (
                            <img src={c.thumb_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-gray-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <input
                            value={c.title}
                            onChange={(e) => patchCandidate(c.key, { title: e.target.value })}
                            className={`${inputCls} w-full mb-1`}
                          />
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {c.author && <span>{c.author}</span>}
                            {c.views > 0 && (
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" /> {formatCount(c.views)}
                              </span>
                            )}
                            {c.likes > 0 && (
                              <span className="flex items-center gap-1">
                                <Heart className="w-3 h-3" /> {formatCount(c.likes)}
                              </span>
                            )}
                          </div>
                          <a
                            href={c.url}
                            target="_blank"
                            rel="noreferrer"
                            className="block text-xs text-brand-400 truncate max-w-[420px] mt-0.5"
                          >
                            {c.url}
                          </a>
                          {c.error && <p className="text-xs text-yellow-500 mt-0.5">{c.error}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 align-top">
                      <select
                        value={c.action_type}
                        onChange={(e) => patchCandidate(c.key, { action_type: e.target.value })}
                        className={selectCls}
                      >
                        {actionOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 align-top">
                      <select
                        value={c.category}
                        onChange={(e) => patchCandidate(c.key, { category: e.target.value })}
                        className={selectCls}
                      >
                        {CATEGORY_OPTIONS.map((o) => (
                          <option key={o} value={o}>{o || '—'}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 align-top">
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={c.reward}
                        onChange={(e) => patchCandidate(c.key, { reward: e.target.value })}
                        className={inputCls}
                      />
                    </td>
                    <td className="p-3 align-top">
                      <input
                        type="number"
                        min="1"
                        value={c.daily_quota}
                        onChange={(e) => patchCandidate(c.key, { daily_quota: e.target.value })}
                        className={inputCls}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCollect;
